import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All Posts');

  useEffect(() => {
    document.title = "Industrial Equipment Blog - ValveSalesSupply | Expert Insights & Industry News";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert insights on industrial equipment, valve purchasing, refinery turnarounds, and process control systems. Professional advice from ValveSalesSupply equipment buyers.');
    }
    
    // Update meta keywords  
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'industrial equipment blog, valve purchasing guide, refinery turnaround, process control systems, equipment buyers');
    }

    // Add Schema.org structured data
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "ValveSalesSupply Industrial Equipment Blog",
      "description": "Expert insights and industry news about industrial equipment purchasing, valve systems, and process controls from professional equipment buyers.",
      "url": "https://valvesalessupply.com/blog",
      "publisher": {
        "@type": "Organization",
        "name": "ValveSalesSupply",
        "url": "https://valvesalessupply.com"
      },
      "blogPost": [
        {
          "@type": "BlogPosting",
          "headline": "Maximizing Value from Your Surplus Industrial Equipment",
          "datePublished": "2024-01-15",
          "author": {
            "@type": "Organization",
            "name": "ValveSalesSupply"
          },
          "url": "https://valvesalessupply.com/blog/maximizing-value-surplus-equipment"
        },
        {
          "@type": "BlogPosting", 
          "headline": "Essential Guide to Refinery Turnaround Equipment Management",
          "datePublished": "2024-01-10",
          "author": {
            "@type": "Organization",
            "name": "ValveSalesSupply"
          },
          "url": "https://valvesalessupply.com/blog/refinery-turnaround-equipment-guide"
        },
        {
          "@type": "BlogPosting",
          "headline": "Control Valve Selection Guide for Process Industries",
          "datePublished": "2024-01-05",
          "author": {
            "@type": "Organization",
            "name": "ValveSalesSupply"
          },
          "url": "https://valvesalessupply.com/blog/control-valve-selection-guide"
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const blogPosts = [
    {
      id: 'maximizing-value-surplus-equipment',
      title: 'Maximizing Value from Your Surplus Industrial Equipment',
      excerpt: 'Learn proven strategies to get the best prices for your surplus valves, instrumentation, and process controls. Professional tips from equipment buyers with 25+ years experience.',
      date: 'January 15, 2024',
      category: 'Equipment Valuation',
      readTime: '8 min read',
      image: 'https://public.readdy.ai/ai/img_res/5f01136344e8a8a6b159ca1594984b15.jpg',
      tags: ['Equipment Sales', 'Valuation', 'Surplus Equipment'],
      hasPage: true
    },
    {
      id: 'refinery-turnaround-equipment-guide',
      title: 'Essential Guide to Refinery Turnaround Equipment Management',
      excerpt: 'Everything you need to know about managing equipment during refinery turnarounds, including removal timing, documentation requirements, and maximizing recovery value.',
      date: 'January 10, 2024', 
      category: 'Refinery Operations',
      readTime: '12 min read',
      image: 'https://readdy.ai/api/search-image?query=refinery%20turnaround%20maintenance%20with%20workers%20removing%20large%20industrial%20valves%20and%20equipment%20systematic%20organized%20process%20professional%20industrial%20photography&width=600&height=400&seq=blog-turnaround-001&orientation=landscape',
      tags: ['Refinery Turnaround', 'Equipment Management', 'Maintenance'],
      hasPage: true
    },
    {
      id: 'control-valve-selection-guide',
      title: 'Control Valve Selection Guide for Process Industries',
      excerpt: 'Comprehensive guide to selecting the right control valves for your process applications. Covers valve types, sizing, materials, and performance considerations.',
      date: 'January 5, 2024',
      category: 'Technical Guide',
      readTime: '10 min read',
      image: 'https://readdy.ai/api/search-image?query=various%20types%20of%20industrial%20control%20valves%20ball%20gate%20globe%20butterfly%20valves%20displayed%20on%20clean%20white%20background%20with%20technical%20specifications%20professional%20product%20photography&width=600&height=400&seq=blog-valves-001&orientation=landscape',
      tags: ['Control Valves', 'Technical Guide', 'Process Engineering'],
      hasPage: true
    },
    {
      id: 'industrial-instrumentation-trends',
      title: 'Industrial Instrumentation Trends for 2024',
      excerpt: 'Explore the latest trends in industrial instrumentation, including smart sensors, IoT integration, predictive maintenance, and digital transformation in process industries.',
      date: 'December 28, 2023',
      category: 'Industry Trends',
      readTime: '7 min read',
      image: 'https://readdy.ai/api/search-image?query=modern%20digital%20industrial%20control%20room%20with%20advanced%20instrumentation%20displays%20smart%20sensors%20and%20IoT%20connected%20devices%20futuristic%20industrial%20technology&width=600&height=400&seq=blog-trends-001&orientation=landscape',
      tags: ['Instrumentation', 'Industry Trends', 'Digital Technology'],
      hasPage: true
    },
    {
      id: 'pressure-gauge-maintenance',
      title: 'Pressure Gauge Maintenance: Best Practices for Industrial Applications',
      excerpt: 'Essential maintenance practices to ensure accuracy and longevity of pressure gauges in industrial environments. Includes calibration schedules and troubleshooting tips.',
      date: 'December 20, 2023',
      category: 'Maintenance',
      readTime: '9 min read',
      image: 'https://readdy.ai/api/search-image?query=industrial%20pressure%20gauges%20being%20calibrated%20and%20maintained%20by%20technician%20with%20professional%20tools%20clean%20laboratory%20setting%20precision%20measurement%20equipment&width=600&height=400&seq=blog-maintenance-001&orientation=landscape',
      tags: ['Pressure Gauges', 'Maintenance', 'Calibration'],
      hasPage: true
    },
    {
      id: 'flow-meter-comparison',
      title: 'Flow Meter Technology Comparison: Choosing the Right Solution',
      excerpt: 'Compare different flow meter technologies including turbine, magnetic, ultrasonic, and Coriolis meters. Learn which technology fits your specific application needs.',
      date: 'December 15, 2023',
      category: 'Technology',
      readTime: '11 min read',
      image: 'https://readdy.ai/api/search-image?query=different%20types%20of%20industrial%20flow%20meters%20turbine%20magnetic%20ultrasonic%20coriolis%20meters%20arranged%20for%20comparison%20technical%20product%20photography%20clean%20industrial%20background&width=600&height=400&seq=blog-flowmeters-001&orientation=landscape',
      tags: ['Flow Meters', 'Technology Comparison', 'Process Measurement'],
      hasPage: true
    }
  ];

  const articles = [
    {
      id: 1,
      title: 'Maximizing Value from Surplus Industrial Equipment',
      excerpt: 'Learn proven strategies to extract maximum value from surplus valves, instrumentation, and process equipment. Discover how proper documentation, timing, and buyer selection can increase recovery by 300%.',
      category: 'Equipment Valuation',
      date: 'January 15, 2024',
      readTime: '8 min read',
      image: 'https://readdy.ai/api/search-image?query=organized%20industrial%20warehouse%20with%20surplus%20valves%20and%20equipment%20neatly%20arranged%20on%20shelves%20professional%20inventory%20management%20clean%20well-lit%20facility&width=600&height=400&seq=blog-surplus-equipment-v2&orientation=landscape',
      slug: '/blog/maximizing-value-surplus-equipment',
    },
    {
      id: 2,
      title: 'Essential Guide to Refinery Turnaround Equipment Management',
      excerpt: 'Complete guide to managing equipment during refinery turnarounds. Learn removal timing, documentation requirements, and strategies to maximize recovery value from surplus equipment.',
      category: 'Refinery Operations',
      date: 'January 10, 2024',
      readTime: '12 min read',
      image: 'https://readdy.ai/api/search-image?query=refinery%20turnaround%20maintenance%20with%20workers%20removing%20large%20industrial%20valves%20and%20equipment%20systematic%20organized%20process%20professional%20industrial%20photography&width=600&height=400&seq=blog-turnaround&orientation=landscape',
      slug: '/blog/refinery-turnaround-equipment-guide',
    },
    {
      id: 3,
      title: 'Control Valve Selection Guide for Process Industries',
      excerpt: 'Comprehensive guide to selecting the right control valves for your process applications. Learn about valve types, sizing, materials, and performance considerations from industry experts.',
      category: 'Technical Guide',
      date: 'January 5, 2024',
      readTime: '10 min read',
      image: 'https://readdy.ai/api/search-image?query=various%20types%20of%20industrial%20control%20valves%20ball%20gate%20globe%20butterfly%20valves%20displayed%20on%20clean%20white%20background%20with%20technical%20specifications%20professional%20product%20photography&width=600&height=400&seq=blog-valves&orientation=landscape',
      slug: '/blog/control-valve-selection-guide',
    },
    {
      id: 4,
      title: '2024 Industrial Instrumentation Technology Trends',
      excerpt: 'Explore the latest trends in industrial instrumentation including IIoT integration, wireless sensors, predictive maintenance, and digital transformation in process industries.',
      category: 'Industry Trends',
      date: 'January 20, 2024',
      readTime: '11 min read',
      image: 'https://readdy.ai/api/search-image?query=modern%20industrial%20control%20room%20with%20digital%20displays%20showing%20real-time%20sensor%20data%20analytics%20dashboards%20and%20wireless%20instrumentation%20technology%20futuristic%20clean%20professional%20lighting&width=600&height=400&seq=blog-trends&orientation=landscape',
      slug: '/blog/industrial-instrumentation-trends',
    },
    {
      id: 5,
      title: 'Pressure Gauge Maintenance Best Practices',
      excerpt: 'Essential maintenance procedures to ensure accurate pressure measurements, extend gauge lifespan, and prevent costly process failures. Complete calibration and troubleshooting guide.',
      category: 'Maintenance',
      date: 'January 25, 2024',
      readTime: '9 min read',
      image: 'https://readdy.ai/api/search-image?query=technician%20calibrating%20industrial%20pressure%20gauge%20with%20test%20equipment%20in%20clean%20workshop%20professional%20maintenance%20procedure%20high%20quality%20photography%20bright%20lighting&width=600&height=400&seq=blog-gauge&orientation=landscape',
      slug: '/blog/pressure-gauge-maintenance',
    },
    {
      id: 6,
      title: 'Flow Meter Technology Comparison Guide',
      excerpt: 'Detailed comparison of major flow meter technologies including magnetic, ultrasonic, vortex, Coriolis, and turbine meters. Learn which technology best fits your application requirements.',
      category: 'Technology',
      date: 'January 30, 2024',
      readTime: '13 min read',
      image: 'https://readdy.ai/api/search-image?query=various%20types%20of%20industrial%20flow%20meters%20displayed%20side%20by%20side%20including%20magnetic%20ultrasonic%20vortex%20and%20Coriolis%20meters%20on%20clean%20white%20background%20professional%20product%20photography%20technical%20comparison&width=600&height=400&seq=blog-flowmeter&orientation=landscape',
      slug: '/blog/flow-meter-comparison',
    },
  ];

  const categories = ['All Posts', 'Equipment Valuation', 'Refinery Operations', 'Technical Guide', 'Industry Trends', 'Maintenance', 'Technology'];

  const filteredPosts = selectedCategory === 'All Posts' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 sm:pb-16 bg-gradient-to-br from-[#1B3A5F] to-[#2B4A6F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center">
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              INDUSTRIAL EQUIPMENT INSIGHTS
            </h1>
            <p className="text-gray-200 text-lg sm:text-xl md:text-2xl max-w-3xl mx-auto mb-8">
              Expert insights, industry trends, and professional advice from experienced equipment buyers
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <div className="relative">
                <input
                  type="search"
                  placeholder="Search articles..."
                  className="w-full px-6 py-4 pl-14 rounded-2xl text-lg border-0 focus:ring-2 focus:ring-white/20 bg-white/10 backdrop-blur-sm text-white placeholder-gray-300"
                />
                <i className="ri-search-line absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-300 text-xl"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="bg-white py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category, index) => (
              <button
                key={index}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-[#1B3A5F] text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No articles found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <article key={post.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="relative">
                    <img
                      src={post.image}
                      alt={`${post.title} - ValveSalesSupply industrial equipment blog featuring professional insights on ${post.tags.join(', ').toLowerCase()}`}
                      className="w-full h-48 object-cover object-top"
                      loading={index > 2 ? "lazy" : "eager"}
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-[#1B3A5F] text-white px-3 py-1 rounded-full text-sm font-medium">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <span>{post.date}</span>
                      <span className="mx-2">•</span>
                      <span>{post.readTime}</span>
                    </div>
                    
                    <h2 className="text-xl font-bold text-[#1B3A5F] mb-3 line-clamp-2">
                      <Link 
                        to={`/blog/${post.id}`}
                        className="hover:text-[#2B4A6F] transition-colors duration-300"
                      >
                        {post.title}
                      </Link>
                    </h2>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    <Link
                      to={`/blog/${post.id}`}
                      className="inline-flex items-center text-[#1B3A5F] font-semibold hover:text-[#2B4A6F] transition-colors duration-300"
                    >
                      Read More
                      <i className="ri-arrow-right-line ml-2"></i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
          
          {/* Load More Button */}
          <div className="text-center mt-12">
            <button className="bg-[#1B3A5F] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#2B4A6F] transition-all duration-300 whitespace-nowrap">
              Load More Articles
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="bg-[#1B3A5F] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h3 className="text-white text-3xl sm:text-4xl font-bold mb-4">
            STAY UPDATED
          </h3>
          <p className="text-gray-200 text-lg sm:text-xl mb-8">
            Get the latest industry insights and equipment buying tips delivered to your inbox
          </p>
          
          <div className="max-w-md mx-auto flex gap-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-6 py-3 rounded-full border-0 focus:ring-2 focus:ring-white/20 text-gray-900"
            />
            <button className="bg-white text-[#1B3A5F] px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap">
              Subscribe
            </button>
          </div>
          
          <p className="text-gray-300 text-sm mt-4">
            No spam. Unsubscribe anytime.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
